import React from 'react';
import { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { GradientHero, GradientTheme } from '@/components/ui/gradient-hero';

interface HeroStat {
  value: string | number;
  label: string;
}

interface GenericPageHeroProps {
  icon: LucideIcon;
  title: string;
  description: string;
  stats?: HeroStat[];
  actionButton?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  heroPrefix?: string; // For unique CSS class prefixes
  enableTranslations?: boolean; // Enable translation support
  titleKey?: string; // Translation key for title
  descriptionKey?: string; // Translation key for description
  // New gradient support
  useGradient?: boolean; // Enable gradient background
  theme?: GradientTheme; // Gradient theme
}

export function GenericPageHero({
  icon: Icon,
  title,
  description,
  stats = [],
  actionButton,
  className,
  heroPrefix = 'generic',
  enableTranslations = false,
  titleKey,
  descriptionKey,
  useGradient = true,
  theme = 'company'
}: GenericPageHeroProps) {
  const { t } = useTranslation('pages');
  
  // Use translations if enabled and keys are provided
  const displayTitle = enableTranslations && titleKey ? t(titleKey) : title;
  const displayDescription = enableTranslations && descriptionKey ? t(descriptionKey) : description;

  // Use GradientHero if gradients are enabled
  if (useGradient) {
    return (
      <GradientHero
        icon={Icon}
        title={displayTitle}
        description={displayDescription}
        theme={theme}
        stats={stats}
        actionButton={actionButton}
        className={className}
        heroPrefix={heroPrefix}
      />
    );
  }

  // Fallback to plain background for backward compatibility
  return (
    <div className={cn(
      `${heroPrefix}-hero-container rounded-lg border bg-card p-6`,
      className
    )}>
      <div className="flex items-center gap-4 flex-wrap">
        <div className={`${heroPrefix}-hero-icon p-2 rounded-md bg-muted`}>
          <Icon className="w-4 h-4 text-muted-foreground" />
        </div>
        
        <div className={`${heroPrefix}-hero-content flex-1 min-w-0`}>
          <h1 className={`${heroPrefix}-hero-title text-lg font-medium mb-1`}>{displayTitle}</h1>
          <p className={`${heroPrefix}-hero-description text-sm text-muted-foreground`}>
            {displayDescription}
          </p>
        </div>
        
        {stats.length > 0 && (
          <div className={`${heroPrefix}-hero-stats flex gap-4 flex-wrap`}>
            {stats.map((stat, index) => (
              <div key={index} className={`${heroPrefix}-hero-stat bg-muted/50 rounded-md p-3 text-center`}>
                <p className={`${heroPrefix}-hero-stat-value text-lg font-semibold text-foreground`}>{stat.value}</p>
                <p className={`${heroPrefix}-hero-stat-label text-xs text-muted-foreground uppercase tracking-wide`}>{stat.label}</p>
              </div>
            ))}
          </div>
        )}
        
        {actionButton && (
          <Button 
            onClick={actionButton.onClick}
            size="sm"
            variant="outline"
            className={`${heroPrefix}-hero-button`}
          >
            {actionButton.label}
          </Button>
        )}
      </div>
    </div>
  );
}